import { AdjustableComponent } from '../../../../utils/hooks/useClasses';
import React, { memo, useState } from 'react';
import { Box, Button, Icons, InputField } from 'plume-ui';
import FormattedMessage from '../../../../utils/components/FormattedMessage';
import { useRecoilValue } from 'recoil';
import { activePartnerAtom } from '../../../../state';
import DownloadHealthWidget from './DownloadHealthWidget';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import {
  getSuggestedFileName,
  validateCsvFilename,
} from '../../../../utils/textUtils';
import { changeKeys } from './helpers';
import { useTrackEvent } from '../../../trackingAnalytics/hooks/useTrackEvent';
import { MixPanelEvents } from '../../../../mixPanelEvents';

type DownloadProps = {};

export type DownloadGenericStyles = {
  root: string;
  title: string;
  menuRow: string;
  summaryCell: string;
};

const Download: AdjustableComponent<DownloadProps, DownloadGenericStyles> = ({
  classes,
}) => {
  const trackEvent = useTrackEvent();
  const activePartner = useRecoilValue(activePartnerAtom);
  const [pending, setPending] = useState(false);
  const [ready, setReady] = useState(false);
  const [downloadData, setDownloadData] = useState([]);
  const [fileName, setFileName] = useState(getSuggestedFileName());
  const { t } = useTranslation();

  const startDownload = () => {
    setPending(true);
    setDownloadData([]);
  };

  const multiMock = false;

  const downloadCallback = (dat: any) => {
    if (multiMock) {
      console.warn('USING MULTIMOCK, DATA SET IS EXPLODED!');
      for (let i = 0; i < 10; i++) {
        dat = [...dat, ...dat];
      }
    }
    setPending(false);
    setReady(true);
    dat = changeKeys(dat);
    setDownloadData(dat);
  };

  const renderWidget = () => {
    return (
      <div>
        <DownloadHealthWidget
          partnerId={activePartner?.id ? activePartner.id : ''}
          callback={(dat: any) => {
            downloadCallback(dat);
          }}
        />
      </div>
    );
  };

  const handleFileName = (fName: string) => {
    setFileName(fName);
  };

  const getFileName = () => {
    return `${fileName}`;
  };

  const handleCancel = () => {
    setPending(false);
    setReady(false);
    setDownloadData([]);
    setFileName(getSuggestedFileName());
  };

  const initiateDownload = () => {
    setPending(false);
    setReady(false);
    setFileName(getSuggestedFileName());
  };

  const renderCsvLink = () => {
    return (
      <CSVLink
        data={downloadData}
        filename={getFileName()}
        className="btn btn-primary"
        target="_blank"
        style={{ textDecoration: 'none' }}
      >
        <Button
          styleVariant="action"
          disabled={validateCsvFilename(fileName)}
          icon={<Icons.DownloadIcon />}
          onClick={() => {
            initiateDownload();
            trackEvent({
              eventName: MixPanelEvents.HEALTH_SUMMARY_DOWNLOAD,
            });
          }}
        >
          <FormattedMessage id="startDownload" />
        </Button>
      </CSVLink>
    );
  };

  const renderDownload = () => {
    return (
      <Box
        style={{
          position: 'absolute',
          top: '10px',
          minWidth: '500px',
          width: '500px',
          right: '-48px',
          zIndex: 99999999,
        }}
      >
        <InputField
          label={t('fileName')}
          name="fileName"
          type="input"
          value={fileName}
          required={false}
          disabled={false}
          onChange={(e) => handleFileName(e.currentTarget.value)}
          messages={[{ status: 'hint', message: t('fileNameHint') }]}
        />
        <div style={{ display: 'flex', marginTop: '16px' }}>
          {renderCsvLink()}
          <Button
            style={{ marginLeft: '12px' }}
            styleVariant="action"
            onClick={() => {
              handleCancel();
            }}
          >
            {t('cancel')}
          </Button>
        </div>
      </Box>
    );
  };

  return (
    <div>
      {ready ? (
        <></>
      ) : (
        <Button
          onClick={() => startDownload()}
          styleVariant="secondary"
          disabled={pending}
          icon={<Icons.DownloadIcon />}
        >
          <FormattedMessage id="healthSummary.download" />
        </Button>
      )}
      <div>{pending ? renderWidget() : <></>}</div>
      <div>{ready ? renderDownload() : <></>}</div>
    </div>
  );
};

export default memo(Download);
